import { TransparentButton } from '@approvalmax/ui';
import { Flex } from '@approvalmax/ui/src/components';
import { errorHelpers, intl, mathService } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { PriceCheckIndicator } from 'modules/request';
import { AddMatchingIcon, DoneIcon } from 'modules/sprites';
import moment from 'moment';
import { isSalesInvoiceDetails } from 'pages/requestForm/utils/typeGuards';
import { FC, memo, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { QBooksMatchingInfo, qBooksMatchingPopupLineItemState } from 'shared/components/(matchings)';

import LineItemFooterSeparator from '../../../components/lineItemsSection/LineItemFooterSeparator';
import LineItemFooterTotal from '../../../components/lineItemsSection/LineItemFooterTotal';
import { LineItemsTable } from '../../../components/lineItemsSection/LineItemsTable';
import OpenInSourceLink from '../../../components/OpenInSourceLink/OpenInSourceLink';
import RbExternalUrl from '../../../components/rbExternalUrl/RbExternalUrl';
import { SectionContainer } from '../../../components/SectionContainer/SectionContainer';
import { checkAccessOnlyAsWatcher } from '../../../selectors/requestSelectors';
import { ColumnDefinition } from '../../../selectors/types/ColumnDefinition';
import LineItemsSection from '../../card/LineItemsSection';
import { messages } from './QBooksLineItemsSection.messages';
import {
    DescriptionCell,
    Footer,
    FooterAmountsTaxType,
    FooterLeftBlock,
    FooterTotalBlock,
    PriceIndicator,
    ReorderableContent,
    StyledExchangeRate,
    UnitPriceCell,
} from './QBooksLineItemsSection.styles';
import { QBooksLineItemsSectionProps } from './QBooksLineItemsSection.types';

const qa = bemFactory.qa('reql-qbooks-line-items-section');

const QBooksLineItemsSection: FC<QBooksLineItemsSectionProps> = memo((props) => {
    const { className, request, readonly, onRemoveMatching, isRemoveMatchingLoading } = props;

    const setLineItem = useSetAtom(qBooksMatchingPopupLineItemState);
    const company = useSelector((state) => selectors.company.findCompanyById(state, request.companyId));
    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;

    const manualMatchingEnabled = request.company.billMatchingSettings?.manualMatchingEnabled ?? false;
    const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
    const isPo = request.integrationCode === domain.IntegrationCode.QBooksPo;
    const isExpense = request.integrationCode === domain.IntegrationCode.QBooksExpense;
    const isSalesInvoice = request.integrationCode === domain.IntegrationCode.QBooksInvoice;

    const isApproved = request.statusV2 === domain.RequestStatusV2.Approved;
    const showManualMatching =
        manualMatchingEnabled &&
        selectors.company.getQBOMatchingIsAvailable(request.company) &&
        (isBill || (isPo && isApproved));
    const isQBOMatchingOnEditingBetaSupported = request.company.betaFeatures.includes(
        domain.CompanyBetaFeature.QBOMatchingOnEditing
    );
    const { details } = request;
    const hasTaxes = details.lineAmountType !== domain.LineAmountType.NoTax;
    const grossRate = details.lineAmountType === domain.LineAmountType.TaxInclusive;
    const priceCheckerSetting = request.company.qboPriceCheckerSettings;

    const discountAmount = useMemo(() => {
        if (!isSalesInvoiceDetails(details)) {
            return 0;
        }

        const discountableAmount = details.lineItems.reduce((acc, { unitPrice, qty }) => {
            if (unitPrice && qty) {
                acc = mathService.add(acc, mathService.multiply(unitPrice, qty));
            }

            return acc;
        }, 0);

        return details.discount
            ? mathService.round(
                  details.discountType === domain.QBooksDiscountType.Amount
                      ? details.discount
                      : mathService.divide(mathService.multiply(discountableAmount, details.discount), 100),
                  2
              )
            : 0;
    }, [details]);

    const isPriceCheckerEnabled =
        (isBill && priceCheckerSetting?.isEnabledForBills) ||
        (isExpense && priceCheckerSetting?.isEnabledForExpenses) ||
        (isPo && priceCheckerSetting?.isEnabledForPurchaseOrders);

    const { id: profileId } = useSelector(selectors.profile.getProfileUser);
    const me = useMemo(
        () => request.company.allMembers.find((user) => user.id === profileId),
        [profileId, request.company.allMembers]
    );
    const hasAccessOnlyAsWatcher = useMemo(
        () => !!me && checkAccessOnlyAsWatcher(me.databaseId || '', me.userEmail, request.company, request),
        [me, request]
    );

    const noItems = details.lineItems.length === 0;

    const taxComponents = useMemo(
        () => details.taxComponents.filter(({ hidden, taxAmount }) => !hidden && typeof taxAmount === 'number'),
        [details.taxComponents]
    );

    const footer = useMemo(() => {
        let taxTypeText;

        switch (details.lineAmountType) {
            case domain.LineAmountType.TaxInclusive:
                taxTypeText = messages.taxTypeTextTaxInclusive;
                break;

            case domain.LineAmountType.TaxExclusive:
                taxTypeText = messages.taxTypeTextTaxExclusive;
                break;

            default:
        }

        const discountType = 'discountType' in details && details.discountType;
        const applyTaxAfterDiscount = 'applyTaxAfterDiscount' in details && details.applyTaxAfterDiscount;

        return (
            <Footer>
                <FooterLeftBlock>
                    <StyledExchangeRate request={request} />

                    <div>
                        {request.origin === domain.RequestOrigin.ReceiptBank && (
                            <RbExternalUrl
                                companyId={request.companyId}
                                requestId={request.id}
                                data-qa={qa('rb-external-url-link')}
                            />
                        )}

                        <OpenInSourceLink
                            url={details.url}
                            editUrl={details.editUrl}
                            statusV2={request.statusV2}
                            integrationType={request.integrationType}
                            integrationCode={request.integrationCode}
                        />
                    </div>
                </FooterLeftBlock>

                <FooterTotalBlock>
                    {(hasTaxes || isSalesInvoice) && (
                        <>
                            <FooterAmountsTaxType data-qa={qa('footer-amounts-tax-type')}>
                                {taxTypeText}
                            </FooterAmountsTaxType>

                            <LineItemFooterTotal
                                qa={qa('subtotal')}
                                label={messages.subTotalText}
                                value={intl.formatNumber(details.subTotal, 2)}
                            />

                            <ReorderableContent
                                direction='column'
                                alignItems='end'
                                spacing='0'
                                $ordered={isUSOrg}
                                $applyTaxAfterDiscount={applyTaxAfterDiscount}
                            >
                                {isSalesInvoiceDetails(details) && (
                                    <>
                                        {Boolean(discountAmount) && (
                                            <LineItemFooterTotal
                                                qa={qa('discount')}
                                                label={
                                                    discountType === domain.QBooksDiscountType.Percent
                                                        ? messages.discountTextPercent({ discount: details.discount })
                                                        : messages.discountText
                                                }
                                                value={`${intl.formatNumber(-discountAmount, 2)}`}
                                            />
                                        )}

                                        {!!details.shipping.price && (
                                            <LineItemFooterTotal
                                                qa={qa('shipping')}
                                                label={messages.shippingText}
                                                value={intl.formatNumber(details.shipping.price, 2)}
                                            />
                                        )}
                                    </>
                                )}

                                {taxComponents.map((taxComponent, index) => {
                                    let invoiceTaxLabelText;

                                    switch (details.lineAmountType) {
                                        case domain.LineAmountType.TaxInclusive:
                                            invoiceTaxLabelText = messages.taxComponentTextTaxInclusive({
                                                percent: taxComponent.taxPercent,
                                                taxableAmount: intl.formatNumber(taxComponent.taxableAmount, 2),
                                            });
                                            break;

                                        case domain.LineAmountType.TaxExclusive:
                                        case domain.LineAmountType.NoTax:
                                            invoiceTaxLabelText = messages.taxComponentTextTaxExclusive({
                                                percent: taxComponent.taxPercent,
                                                taxableAmount: intl.formatNumber(taxComponent.taxableAmount, 2),
                                            });
                                            break;

                                        default:
                                            throw errorHelpers.notSupportedError();
                                    }

                                    return (
                                        <LineItemFooterTotal
                                            qa={qa(invoiceTaxLabelText)}
                                            key={index}
                                            label={invoiceTaxLabelText}
                                            value={intl.formatNumber(taxComponent.taxAmount, 2)}
                                        />
                                    );
                                })}
                            </ReorderableContent>
                        </>
                    )}

                    {request.currency !== request.company.defaultCurrency ? (
                        <>
                            <LineItemFooterTotal
                                label={messages.totalRequestCurrencyText({ currency: request.currency })}
                                value={intl.formatNumber(request.amount, 2)}
                                qa={qa('footer-total-request-currency')}
                            />

                            <LineItemFooterSeparator />

                            <LineItemFooterTotal
                                label={messages.totalCompanyCurrencyText({
                                    companyCurrency: request.company.defaultCurrency,
                                })}
                                value={intl.formatNumber(request.amountInBaseCurrency, 2)}
                                qa={qa('footer-total-company-currency')}
                            />
                        </>
                    ) : (
                        <>
                            <LineItemFooterSeparator />

                            <LineItemFooterTotal
                                label={messages.totalText({ currency: request.currency })}
                                value={intl.formatNumber(request.amount, 2)}
                                qa={qa('footer-total-request-currency')}
                            />
                        </>
                    )}
                </FooterTotalBlock>
            </Footer>
        );
    }, [details, discountAmount, hasTaxes, isSalesInvoice, isUSOrg, request, taxComponents]);

    const table = useMemo(() => {
        const columnDefs: Array<ColumnDefinition<domain.QBooksLineItem>> = [];

        if (isSalesInvoice) {
            columnDefs.push({
                id: 'serviceDate',
                name: messages.serviceDateColumnName,
                value: (li) => (li.serviceDate ? moment.utc(li.serviceDate).format('ll') : null),
            });
        }

        columnDefs.push(
            {
                id: 'item',
                name: messages.itemColumnName,
                value: (li) => (li.item ? li.item.text : null),
            },
            {
                id: 'description',
                name: messages.descriptionColumnName,
                value: (li) => li.description,
                cell: (value) => <DescriptionCell>{value}</DescriptionCell>,
            },
            {
                id: 'qty',
                name: messages.qtyColumnName,
                value: (li) => intl.formatNumber(li.qty || 0, 'auto'),
                alignRight: true,
            },
            {
                id: 'unitPrice',
                name: messages.unitPriceColumnName,
                value: (li) => {
                    const value = grossRate ? li.unitPriceGross : li.unitPrice;

                    return intl.formatNumber(value || 0, 'auto');
                },
                alignRight: true,
                cell: (value, li) => {
                    const unitPrice = grossRate ? li.unitPriceGross : li.unitPrice;

                    return (
                        <PriceCheckIndicator
                            actualPrice={unitPrice}
                            expectedPrice={typeof li.defaultRate === 'number' ? li.defaultRate : undefined}
                            disabled={!isPriceCheckerEnabled}
                            integrationType={domain.IntegrationType.QBooks}
                            integrationCode={request.integrationCode}
                            isMultiCurrency={request.currency !== request.companyCurrency}
                        >
                            {({ warningLevel }) => (
                                <UnitPriceCell>
                                    {value}

                                    {warningLevel && <PriceIndicator $warningLevel={warningLevel} />}
                                </UnitPriceCell>
                            )}
                        </PriceCheckIndicator>
                    );
                },
            }
        );

        if (isBill || isExpense) {
            columnDefs.push({
                id: 'billable',
                name: messages.billableColumnName,
                value: (li) => {
                    return li.isBillable ? messages.billableColumnTooltip : null;
                },
                alignRight: false,
                width: 50,
                cell: (val) =>
                    val && (
                        <Flex alignItems='center' justifyContent='center'>
                            <DoneIcon width={13} height={10} />
                        </Flex>
                    ),
            });
        }

        if (isBill || isExpense || (isSalesInvoice && isUSOrg)) {
            columnDefs.push({
                id: 'taxable',
                name: messages.taxableColumnName,
                value: (li) => {
                    return li.isTaxable ? messages.taxableColumnTooltip : null;
                },
                alignRight: false,
                width: 30,
                cell: (val) =>
                    val && (
                        <Flex alignItems='center' justifyContent='center'>
                            <DoneIcon width={13} height={10} />
                        </Flex>
                    ),
            });
        }

        if (hasTaxes) {
            columnDefs.push({
                id: 'taxCodeName',
                name: messages.taxColumnName,
                value: (li) => (li.taxCode ? li.taxCode.text : null),
            });
        }

        columnDefs.push(
            {
                id: 'customerName',
                name: messages.customerColumnName,
                value: (li) => (li.customer ? li.customer.text : null),
            },
            {
                id: 'className',
                name: messages.classColumnName,
                value: (li) => (li.class ? li.class.text : null),
            },
            {
                id: 'amount',
                name: messages.amountColumnName({ currency: request.currency }),
                value: (li) => intl.formatNumber(li.amount || 0),
                ...(!showManualMatching && {
                    width: 90,
                    alignRight: true,
                    alwaysVisible: true,
                }),
            }
        );

        if (showManualMatching) {
            const matchingCommands = selectors.request.getQBooksMatchingCommands(
                request,
                profileId,
                hasAccessOnlyAsWatcher
            );

            columnDefs.push({
                id: 'linked',
                name: messages.linkedColumnName,
                value: () => messages.matching,
                cell: (_val, li) => {
                    if (readonly) {
                        return null;
                    }

                    const isMatched = isQBOMatchingOnEditingBetaSupported
                        ? Boolean(li.matchedLineItemIds?.length || li.matchedLineItemId)
                        : li.isMatched;

                    if (isMatched) {
                        return (
                            <QBooksMatchingInfo
                                lineItemId={li.id}
                                profileId={profileId}
                                request={request}
                                onRemoveMatching={onRemoveMatching}
                                isRemoveMatchingLoading={isRemoveMatchingLoading}
                            />
                        );
                    }

                    if (matchingCommands.addMatching.disabled) {
                        return '—';
                    }

                    return (
                        <TransparentButton execute={() => setLineItem(li)} command={matchingCommands.addMatching}>
                            <AddMatchingIcon />
                        </TransparentButton>
                    );
                },
                width: 40,
                alignRight: true,
                alwaysVisible: true,
            });
        }

        if (noItems) {
            return null;
        }

        return <LineItemsTable columnDefinitions={columnDefs} lineItems={details.lineItems} />;
    }, [
        isSalesInvoice,
        isBill,
        isExpense,
        isUSOrg,
        hasTaxes,
        request,
        showManualMatching,
        noItems,
        details.lineItems,
        grossRate,
        isPriceCheckerEnabled,
        profileId,
        hasAccessOnlyAsWatcher,
        readonly,
        isQBOMatchingOnEditingBetaSupported,
        onRemoveMatching,
        isRemoveMatchingLoading,
        setLineItem,
    ]);

    return (
        <SectionContainer className={className} title={noItems ? null : messages.sectionHeaderText}>
            <LineItemsSection qa={qa()} renderTable={() => table} renderFooter={() => footer} />
        </SectionContainer>
    );
});

export default QBooksLineItemsSection;
